import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import colors from 'theme/colors'
import breakpoints from 'theme/breakpoints'
import BasicInfo from './BasicInfo'
import ImageProfile from './ImageProfile'
import ImageBackground from './ImageBackground'
import Button from 'components/_UI/Button'
import Loader from 'components/_UI/Loader'
import Text from 'components/_UI/Text'
import imageUploadApi from 'api/requests/imageUpload'
import userApi from 'api/requests/user'
import unformat from 'helpers/unformat'
import { useUser } from 'context/user/User'
import { useCollapse } from 'context/general/Collapse'
import Users from '../Users'
import validateInputFile from 'helpers/validate-input-file'
import allowedFilesTypes from 'texts/messages/allowedFilesTypes'
import { X } from 'lucide-react'

export default function EditProfile({ data, mobile, close = () => false, inline, onClose }) {
    const { user, setUser } = useUser()
    const { collapse, setCollapse } = useCollapse()

    const effectiveData = data || user

    const [imagePath, setImagePath] = useState(null)
    const [backgroundPath, setBackgroundPath] = useState(null)
    const [errorImage, setErrorImage] = useState(false)
    const [errorImageBackground, setErrorImageBackground] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState(false)
    const [isUpdateImage, setUpdateImage] = useState(false)
    const [errorTypeImageProfile, setErrorTypeImageProfile] = useState(false)
    const [errorTypeImageBackground, setErrorTypeImageBackground] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: !effectiveData ? {} : {
            firstName: effectiveData?.first_name,
            lastName: effectiveData?.last_name,
            description: effectiveData?.bio,
            username: effectiveData?.username,
        }
    })

    useEffect(() => {
        const src = effectiveData
        if (src) {
            reset({
                username: src?.username,
                firstName: src?.first_name,
                lastName: src?.last_name,
                description: src?.bio,
            })
            setBackgroundPath(src?.background_photo_id)
            setImagePath(src?.avatar_id)
        }
    }, [effectiveData?.id])

    async function uploadImage(file) {
        setUpdateImage(true)
        const validationFile = validateInputFile(file)
        if (!validationFile.isValid) {
            setErrorTypeImageProfile(validationFile.message)
            setUpdateImage(false)
            return
        }
        const formData = new FormData()
        formData.append('image', file)
        formData.append('folder', 'users_photo')
        try {
            const { data: res } = await imageUploadApi.uploadWithFolder(formData)
            setImagePath(res.id)
        } catch (error) {
            setErrorImage(true)
        } finally {
            setUpdateImage(false)
        }
    }

    async function uploadImageBackground(file) {
        setUpdateImage(true)
        const validationFile = validateInputFile(file)
        if (!validationFile.isValid) {
            setErrorTypeImageBackground(validationFile.message)
            setUpdateImage(false)
            return
        }
        const formData = new FormData()
        formData.append('image', file)
        formData.append('folder', 'users_background')
        try {
            const { data: res } = await imageUploadApi.uploadWithFolder(formData)
            setBackgroundPath(res.id)
        } catch (error) {
            setErrorImageBackground(true)
        } finally {
            setUpdateImage(false)
        }
    }

    async function onToggleDelete() {
        setErrorTypeImageProfile(false)
        setErrorImage(false)
        try {
            await imageUploadApi.delete(imagePath)
            setImagePath(null)
        } catch (error) {
            console.log(error)
        }
    }

    async function onToggleDeleteBackground() {
        setErrorTypeImageBackground(false)
        setErrorImageBackground(false)
        try {
            await imageUploadApi.delete(backgroundPath)
            setBackgroundPath(null)
        } catch (error) {
            console.log(error)
        }
    }

    const editProfile = async (content) => {
        setIsLoading(true)
        const userId = effectiveData?.id

        const payload = {
            id: userId,
            username: content?.username,
            first_name: content?.firstName,
            last_name: content?.lastName,
            bio: content?.description,
            avatar_id: imagePath,
            background_photo_id: backgroundPath,
            ...(!effectiveData?.document && content?.cpf && {
                document: unformat.getNumbers(content.cpf)
            })
        }

        try {
            const { data: res } = await userApi.update(userId, payload)
            setUser(res?.data)

            if (inline) {
                onClose?.()
            } else if (mobile) {
                close()
            } else {
                setCollapse({
                    ...collapse,
                    withContent: true,
                    height: 'max',
                    arrowPosition: false,
                    componentToRender: () => <Users home />
                })
            }
        } catch (error) {
            setErrorMessage('Ocorreu um erro inesperado')
        } finally {
            setIsLoading(false)
        }
    }

    if (inline) {
        if (!effectiveData) {
            return <InlineWrapper><InlineCard><Loader /></InlineCard></InlineWrapper>
        }

        return (
            <InlineWrapper>
                <InlineCard>
                    <InlineSectionTitle>Fotos</InlineSectionTitle>

                    <ImageBackground
                        isEdit={effectiveData}
                        onToggleImage={uploadImageBackground}
                        onDeleteFile={onToggleDeleteBackground}
                        errorImageBackground={errorImageBackground}
                        errorTypeImageBackground={errorTypeImageBackground}
                        setErrorImageBackground={setErrorImageBackground}
                        setErrorTypeImageBackground={setErrorTypeImageBackground}
                        allowedFiles={allowedFilesTypes.images}
                    />

                    <ImageProfile
                        isEdit={effectiveData}
                        onToggleImage={uploadImage}
                        onDeleteFile={onToggleDelete}
                        setErrorImage={setErrorImage}
                        errorTypeImageProfile={errorTypeImageProfile}
                        setErrorTypeImageProfile={setErrorTypeImageProfile}
                        allowedFiles={allowedFilesTypes.images}
                    />

                    <FormSection onSubmit={handleSubmit(editProfile)}>
                        <BasicInfo
                            register={register}
                            errors={errors}
                            user={effectiveData || user}
                        />

                        {errorMessage && (
                            <ErrorMessage>{errorMessage}</ErrorMessage>
                        )}

                        <ActionRow>
                            <CancelButton type='button' onClick={() => onClose?.()}>
                                Cancelar
                            </CancelButton>

                            {isLoading ? (
                                <Loader />
                            ) : (
                                <SaveButton type='submit' disabled={isUpdateImage}>
                                    Salvar alterações
                                </SaveButton>
                            )}
                        </ActionRow>
                    </FormSection>
                </InlineCard>
            </InlineWrapper>
        )
    }

    return (
        <ContainerForm onSubmit={handleSubmit(editProfile)}>
            {mobile && (
                <StyledButton>
                    <ContentCloseButtom onClick={() => close()}>
                        <X size={18} color={colors.night} />
                    </ContentCloseButtom>
                </StyledButton>
            )}

            <Text name='title' color={colors.nightDark} marginTop={'20px'}>Editar Perfil</Text>

            <ImageBackground
                isEdit={effectiveData}
                onToggleImage={(e) => uploadImageBackground(e)}
                onDeleteFile={() => onToggleDeleteBackground()}
                errorImageBackground={errorImageBackground}
                errorTypeImageBackground={errorTypeImageBackground}
                setErrorImageBackground={setErrorImageBackground}
                setErrorTypeImageBackground={setErrorTypeImageBackground}
                allowedFiles={allowedFilesTypes.images}
            />

            <ImageProfile
                isEdit={effectiveData}
                onToggleImage={(e) => uploadImage(e)}
                onDeleteFile={() => onToggleDelete()}
                setErrorImage={setErrorImage}
                errorTypeImageProfile={errorTypeImageProfile}
                setErrorTypeImageProfile={setErrorTypeImageProfile}
                allowedFiles={allowedFilesTypes.images}
            />

            <BasicInfo
                register={register}
                errors={errors}
                user={effectiveData || user}
            />

            {errorMessage && (
                <ErrorCentered>
                    <Text color={colors.danger} type='bold'>{errorMessage}</Text>
                </ErrorCentered>
            )}

            {!isLoading ? (
                <ContentButton>
                    <Button
                        category={'squired'}
                        variant={isUpdateImage ? 'disabeld' : 'primary'}
                        type='submit'
                    >
                        Editar perfil
                    </Button>
                </ContentButton>
            ) : (
                <Loader />
            )}
        </ContainerForm>
    )
}

/* ── Inline styles ─────────────────────────────────────────── */

const InlineWrapper = styled.div`
    padding: 8px 0 0;

    @media screen and (min-width: ${breakpoints.sm}) {
        padding: 8px 0 24px;
    }
`

const InlineCard = styled.div`
    background: ${colors.neutralLight};
    box-shadow: 0 1px 4px rgba(0,0,0,0.08), 0 4px 20px rgba(0,0,0,0.04);
    padding: 24px 20px;

    @media screen and (min-width: ${breakpoints.sm}) {
        max-width: 640px;
        margin: 0 auto;
        border-radius: 14px;
    }
`

const InlineSectionTitle = styled.p`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${colors.nightLight};
    margin: 0 0 4px;
`

const FormSection = styled.form`
    margin-top: 8px;
`

const ErrorMessage = styled.p`
    font-size: 13px;
    color: ${colors.danger};
    text-align: center;
    margin: 8px 0;
`

const ActionRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid ${colors.neutralDark};
`

const CancelButton = styled.button`
    padding: 10px 20px;
    border-radius: 8px;
    border: 1.5px solid ${colors.nightLight};
    background: transparent;
    color: ${colors.night};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        border-color: ${colors.night};
        color: ${colors.nightDark};
    }
`

const SaveButton = styled.button`
    padding: 10px 24px;
    border-radius: 8px;
    border: none;
    background: ${colors.occasionalPurple};
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`

/* ── Legacy (non-inline) styles ────────────────────────────── */

const ContainerForm = styled.form`
    padding: 10px 20px 20px 20px;
    background: ${colors.neutralBackground};
    display: flex;
    flex-direction: column;
    align-items: center;
`

const ContentButton = styled.div`
    width: 100%;

    @media screen and (min-width: ${breakpoints.sm}) {
        width: 500px;
        margin-top: 20px;
    }
`

const StyledButton = styled.div`
    flex-direction: row;
    width: 100%;
    display: flex;
    justify-content: flex-end;
`

const ContentCloseButtom = styled.div`
    width: 30px;
    height: 30px;
    padding: 4px;
    border-radius: 50%;
    border: 1px solid ${colors.nightLight};
    background: ${colors.neutralLight};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
`

const ErrorCentered = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 6px;
`
